/**
 * "My tickets" — protected list of the user's bookings.
 *
 * The mobile-app contract uses the persisted browser `app_id` (UUID stored
 * in localStorage) as the identity for ticket retrieval. Because that
 * identity follows the browser rather than the account, we lean on a soft
 * gate: the page is visible to anonymous users but shows a login prompt
 * + a CTA back to the booking flow. Logged-in users see a 2-tab list of
 * Active and History tickets.
 *
 * `cancelTicket` is wired but the active backend endpoint contract is
 * still pending — the API call will surface any backend error to the user.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useAuth from "../helper/useAuth";
import { useTheme } from "../components/ThemeProvider";
import {
  cancelTicket,
  createDelayTicket,
  fetchActiveTickets,
  fetchCanceledTickets,
  fetchFinishedTickets,
} from "../helper/bookingApi";
import { resolveImageUrl, getBookingPalette } from "../components/booking/BookingTheme";
import {
  EmptyState,
  RetryButton,
  StepCard,
} from "../components/booking/BookingPrimitives";
import {
  formatClockTime,
  formatDayTimeLabel,
  parseSlotDate,
} from "../components/booking/bookingFormat";

const TAB_ACTIVE = "active";
const TAB_HISTORY = "history";
const TAB_CANCELED = "canceled";
const TICKET_TABS = [TAB_ACTIVE, TAB_HISTORY, TAB_CANCELED];
const DELAY_MINUTES = [10, 20, 30, 40];

function getFirstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function makeTicketUrlToken(id, label) {
  if (id == null || id === "") return "";
  const safeLabel = String(label || id)
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${encodeURIComponent(safeLabel || id)}`;
}

function makeTicketQueryToken(id, label) {
  if (id == null || id === "") return "";
  const safeLabel = String(label || id)
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${safeLabel || id}`;
}

function getTicketOrderId(ticket) {
  return getFirstValue(ticket?.id, ticket?.order_ticket_id, ticket?.orderTicketId);
}

function extractDelayRecommendations(payload) {
  const value = payload?.data?.data || payload?.data?.times || payload?.data;
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.values(value).flatMap((item) => (Array.isArray(item) ? item : [item]));
  }
  return [];
}

function formatDelayRecommendation(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return (
    item.time ||
    item.start_time ||
    item.startTime ||
    item.req_date ||
    item.date ||
    item.label ||
    ""
  );
}

function buildTicketBookingHref(ticket, { basePath, action = "repeat" } = {}) {
  const orgId = getFirstValue(
    ticket?.organization_id,
    ticket?.organizationId,
    ticket?.org_id,
    ticket?.orgId,
  );
  const branchId = getFirstValue(ticket?.branch_id, ticket?.branchId);
  const categoryId = getFirstValue(ticket?.category_id, ticket?.categoryId);
  const workerId = getFirstValue(ticket?.worker_id, ticket?.workerId, ticket?.user_id, ticket?.userId);
  const orgName = ticket?.organizationName || ticket?.organization_name || ticket?.orgName;
  const branchName = ticket?.branchName || ticket?.branch_name || orgName;
  const categoryName = ticket?.categoryName || ticket?.category_name;
  const workerName = ticket?.workerName || ticket?.worker_name || ticket?.userName || ticket?.user_name;

  let path = basePath;
  const params = new URLSearchParams();
  params.set("step", "time");
  params.set("source", action);

  if (orgId) {
    path += `/${makeTicketUrlToken(orgId, orgName)}`;
    if (branchId) path += `/${makeTicketUrlToken(branchId, branchName)}`;
  } else if (branchId) {
    params.set("branch", makeTicketQueryToken(branchId, branchName));
    if (orgName) params.set("orgName", orgName);
  }

  if (categoryId) params.set("category", makeTicketQueryToken(categoryId, categoryName));
  if (workerId) {
    params.set("mode", "service");
    params.set("sub", "workers");
    params.set("worker", makeTicketQueryToken(workerId, workerName));
  }
  if (action === "reschedule") {
    const orderId = getTicketOrderId(ticket);
    if (orderId) params.set("reschedule", String(orderId));
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function formatTicketDate(value, lang, t) {
  const d = parseSlotDate(value);
  if (!d) return value ? String(value) : "";
  return formatDayTimeLabel(d, lang, t);
}

function formatTicketClock(value) {
  const d = parseSlotDate(value);
  if (!d) return "";
  return formatClockTime(d).replace(":", "։");
}

function extractTickets(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.order_tickets)) return payload.data.order_tickets;
  if (Array.isArray(payload?.data?.mobile)) return payload.data.mobile;
  if (Array.isArray(payload?.order_tickets) && payload.order_tickets.length) {
    return payload.order_tickets;
  }
  if (Array.isArray(payload?.mobile) && payload.mobile.length) {
    return payload.mobile;
  }
  if (Array.isArray(payload?.order_tickets)) return payload.order_tickets;
  if (Array.isArray(payload?.mobile)) return payload.mobile;
  return [];
}

function TicketInfoChip({ palette, icon, label, value }) {
  if (!value) return null;
  return (
    <div
      style={{
        border: palette.border,
        borderRadius: 14,
        padding: "10px 12px",
        background: palette.surfaceAlt,
        minWidth: 0,
      }}
    >
      <div
        className="d-flex align-items-center gap-2"
        style={{
          color: palette.textMuted,
          fontSize: "0.74rem",
          marginBottom: 3,
        }}
      >
        <i className={icon} aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div
        className="fw-700"
        style={{
          color: palette.text,
          fontSize: "0.92rem",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function TicketActionButton({
  as: Component = "button",
  palette,
  icon,
  label,
  tone = "neutral",
  ...props
}) {
  const colors = {
    neutral: {
      bg: palette.surfaceAlt,
      border: palette.border,
      color: palette.text,
    },
    accent: {
      bg: palette.accentSoft,
      border: `1px solid ${palette.accentBorder}`,
      color: palette.accent,
    },
    danger: {
      bg: "rgba(239,68,68,0.1)",
      border: "1px solid rgba(239,68,68,0.42)",
      color: "hsl(0 75% 58%)",
    },
  }[tone];

  return (
    <Component
      className="fw-600"
      style={{
        background: colors.bg,
        border: colors.border,
        color: colors.color,
        borderRadius: 14,
        padding: "9px 12px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        textDecoration: "none",
        fontSize: "0.86rem",
        minHeight: 40,
        cursor: "pointer",
      }}
      {...props}
    >
      <i className={icon} aria-hidden="true" />
      <span>{label}</span>
    </Component>
  );
}

function TicketCard({
  palette,
  ticket,
  status,
  lang,
  onCancel,
  onDelay,
  repeatHref,
  rescheduleHref,
}) {
  const { t } = useTranslation();
  const orgImage = resolveImageUrl(ticket?.organizationImage);
  const branchName = ticket?.branchName || ticket?.organizationName || "";
  const branchAddress = ticket?.branchAddress || "";
  const categoryName = ticket?.categoryName || "";
  const date = formatTicketDate(ticket?.date, lang, t);
  const orgName = ticket?.organizationName || "";
  const startedAt = formatTicketDate(ticket?.start_ticket, lang, t);
  const doneAt = formatTicketDate(ticket?.done_ticket, lang, t);
  const ticketCode = `${ticket?.code || ""}${ticket?.number || ""}` || ticket?.code || "";
  const workerLabel = ticket?.worker_id ? `#${ticket.worker_id}` : "";
  const isLightTicket = palette.surface === "hsl(0 0% 100%)";
  const ticketHeaderBg = orgImage
    ? isLightTicket
      ? `linear-gradient(115deg, rgba(37,99,235,0.92), rgba(10,200,160,0.82)), url(${orgImage}) center/cover`
      : `linear-gradient(90deg, rgba(10,14,24,0.9), rgba(10,14,24,0.58)), url(${orgImage}) center/cover`
    : "linear-gradient(135deg, rgba(35,124,231,0.92), rgba(9,200,163,0.86))";
  const statusBadgeStyle = isLightTicket
    ? {
        background: "rgba(255, 255, 255, 0.94)",
        color: "hsl(166 100% 25%)",
        border: "1px solid rgba(255,255,255,0.92)",
        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
      }
    : {
        background: "rgba(255,255,255,0.16)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.28)",
      };

  const statusKey =
    status === TAB_ACTIVE
      ? "statusActive"
      : status === TAB_CANCELED
        ? "statusCanceled"
        : ticket?.status === "missed"
          ? "statusMissed"
          : "statusFinished";

  return (
    <div
      className="booking-ticket-card"
      style={{
        background: `linear-gradient(180deg, ${palette.surface} 0%, ${palette.surfaceAlt} 100%)`,
        border: palette.border,
        borderRadius: 20,
        overflow: "hidden",
        display: "grid",
        boxShadow: "0 18px 45px rgba(0,0,0,0.18)",
      }}
    >
      <div
        className="booking-ticket-card__hero"
        style={{
          minHeight: 144,
          padding: "clamp(16px, 4vw, 22px)",
          background: ticketHeaderBg,
          color: "#fff",
        }}
      >
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <span
              style={{
                width: "clamp(52px, 11vw, 66px)",
                height: "clamp(52px, 11vw, 66px)",
                borderRadius: 18,
                flexShrink: 0,
                background: "rgba(255,255,255,0.94)",
                color: palette.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "2rem",
                boxShadow: "0 12px 28px rgba(0,0,0,0.22)",
              }}
            >
              <i className="las la-receipt" aria-hidden="true" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                className="fw-700"
                style={{
                  fontSize: "clamp(1.1rem, 3vw, 1.5rem)",
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                }}
              >
                {orgName || branchName || t("booking.tickets.title")}
              </div>
              {branchName ? (
                <div
                  className="d-flex align-items-center gap-1 mt-1"
                  style={{ color: "rgba(255,255,255,0.82)", fontSize: "0.9rem" }}
                >
                  <i className="las la-map-marker" aria-hidden="true" />
                  <span>{branchName}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <span
              className="badge fw-700"
              style={{
                ...statusBadgeStyle,
                padding: "7px 12px",
                borderRadius: 999,
              }}
            >
              {t(`booking.tickets.card.${statusKey}`)}
            </span>
            <span
              className="fw-700"
              style={{
                color: palette.accent,
                background: "rgba(10,14,24,0.78)",
                border: `1px solid ${palette.accent}`,
                borderRadius: 999,
                padding: "8px 14px",
                fontSize: "clamp(1rem, 3vw, 1.35rem)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              #{ticketCode || "--"}
            </span>
          </div>
        </div>
      </div>

      <div style={{ padding: "clamp(14px, 3vw, 18px)", display: "grid", gap: 14 }}>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <div
              className="fw-700"
              style={{ color: palette.text, fontSize: "clamp(1rem, 2.5vw, 1.18rem)" }}
            >
              {categoryName || t("booking.tickets.card.service")}
            </div>
            {date ? (
              <div
                className="d-flex align-items-center gap-1"
                style={{ color: palette.textMuted, fontSize: "0.9rem", marginTop: 3 }}
              >
                <i className="las la-clock" aria-hidden="true" />
                <span>{date}</span>
              </div>
            ) : null}
          </div>
          {workerLabel ? (
            <span
              className="pill fw-600"
              style={{
                border: palette.border,
                color: palette.accent,
                background: palette.accentSoft,
                padding: "7px 12px",
              }}
            >
              <i className="las la-user-tie me-1" aria-hidden="true" />
              {t("booking.tickets.card.specialist")} {workerLabel}
            </span>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 10,
          }}
        >
          <TicketInfoChip palette={palette} icon="las la-concierge-bell" label={t("booking.tickets.card.service")} value={categoryName} />
          <TicketInfoChip palette={palette} icon="las la-calendar-check" label={t("booking.tickets.card.datetime")} value={date} />
          <TicketInfoChip palette={palette} icon="las la-play-circle" label={t("booking.tickets.card.startedAt")} value={startedAt} />
          <TicketInfoChip palette={palette} icon="las la-check-circle" label={t("booking.tickets.card.doneAt")} value={doneAt} />
          <TicketInfoChip palette={palette} icon="las la-map-marker-alt" label={t("booking.tickets.card.branch")} value={branchName || branchAddress} />
          <TicketInfoChip palette={palette} icon="las la-hashtag" label={t("booking.tickets.card.queueNumber")} value={ticket?.number ? String(ticket.number) : ""} />
        </div>

        {status === TAB_ACTIVE ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 8,
            }}
          >
            {rescheduleHref ? (
              <TicketActionButton
                as={Link}
                to={rescheduleHref}
                target="_blank"
                rel="noopener noreferrer"
                palette={palette}
                tone="accent"
                icon="las la-calendar-plus"
                label={t("booking.tickets.card.rescheduleBtn")}
              />
            ) : null}
            {typeof onDelay === "function" ? (
              <TicketActionButton
                type="button"
                onClick={() => onDelay(ticket)}
                palette={palette}
                icon="las la-clock"
                label={t("booking.tickets.card.delayBtn")}
              />
            ) : null}
            {typeof onCancel === "function" ? (
              <TicketActionButton
                type="button"
                onClick={() => onCancel(ticket)}
                palette={palette}
                tone="danger"
                icon="las la-times"
                label={t("booking.tickets.card.cancelBtn")}
              />
            ) : null}
          </div>
        ) : null}

        {status !== TAB_ACTIVE && repeatHref ? (
          <div className="d-flex justify-content-end">
            <Link
              to={repeatHref}
              target="_blank"
              rel="noopener noreferrer"
              className="pill fw-600"
              style={{
                background: palette.accent,
                border: "none",
                color: "#fff",
                padding: "9px 18px",
                fontSize: "0.9rem",
                textDecoration: "none",
              }}
            >
              <i className="las la-redo-alt me-1" aria-hidden="true" />
              {t("booking.tickets.card.repeatBtn")}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TicketModal({ palette, title, message, icon, children, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ticket-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(10, 14, 24, 0.68)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "18px",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          width: "min(100%, 520px)",
          background: palette.surface,
          border: palette.border,
          borderRadius: 22,
          padding: "clamp(20px, 4vw, 28px)",
          boxShadow: "0 28px 70px rgba(0,0,0,0.32)",
          color: palette.text,
        }}
      >
        <div className="d-flex justify-content-end">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: palette.border,
              background: palette.surfaceAlt,
              color: palette.textMuted,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="las la-times" aria-hidden="true" />
          </button>
        </div>
        <div className="text-center" style={{ marginTop: -6 }}>
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: "50%",
              margin: "0 auto 14px",
              background: palette.accentSoft,
              color: palette.accent,
              border: `1px solid ${palette.accentBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "2.2rem",
            }}
          >
            <i className={icon} aria-hidden="true" />
          </div>
          <h3
            id="ticket-modal-title"
            className="fw-700 mb-2"
            style={{ color: palette.text, fontSize: "clamp(1.2rem, 3vw, 1.45rem)" }}
          >
            {title}
          </h3>
          {message ? (
            <p
              className="mb-0"
              style={{
                color: palette.textMuted,
                fontSize: "0.95rem",
                lineHeight: 1.55,
              }}
            >
              {message}
            </p>
          ) : null}
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

const MyTicketsPage = ({ setOpenRegisterModal } = {}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { isAuthenticated } = useAuth();
  const { isDarkMode } = useTheme();
  const isVisuallyDark = !isDarkMode;
  const palette = useMemo(() => getBookingPalette(isVisuallyDark), [isVisuallyDark]);

  const [tab, setTab] = useState(TAB_ACTIVE);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [canceled, setCanceled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccessOpen, setCancelSuccessOpen] = useState(false);
  const [lastCanceledTicket, setLastCanceledTicket] = useState(null);
  const [delayTarget, setDelayTarget] = useState(null);
  const [delayMinutes, setDelayMinutes] = useState(DELAY_MINUTES[0]);
  const [delayLoading, setDelayLoading] = useState(false);
  const [delayError, setDelayError] = useState(null);
  const [delaySuccess, setDelaySuccess] = useState(null);
  const [delayRecommendations, setDelayRecommendations] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [activeRes, finishedRes, canceledRes] = await Promise.all([
        fetchActiveTickets(lang),
        fetchFinishedTickets(lang),
        fetchCanceledTickets(lang),
      ]);
      setActive(extractTickets(activeRes));
      setHistory(extractTickets(finishedRes));
      setCanceled(extractTickets(canceledRes));
    } catch (err) {
      console.error("[booking] tickets load failed", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async (ticket) => {
    if (!getTicketOrderId(ticket)) return;
    setCancelTarget(ticket);
    setCancelError(null);
  };

  const handleDelay = (ticket) => {
    if (!getTicketOrderId(ticket)) return;
    setDelayTarget(ticket);
    setDelayMinutes(DELAY_MINUTES[0]);
    setDelayError(null);
    setDelaySuccess(null);
    setDelayRecommendations([]);
  };

  const handleConfirmCancel = async () => {
    if (!getTicketOrderId(cancelTarget) || cancelLoading) return;
    setCancelLoading(true);
    setCancelError(null);
    try {
      const target = cancelTarget;
      await cancelTicket(lang, getTicketOrderId(target));
      setLastCanceledTicket(target);
      setCancelTarget(null);
      setCancelSuccessOpen(true);
      // Re-fetch so both lists reflect the change immediately.
      await load();
    } catch (err) {
      console.error("[booking] cancel failed", err);
      setCancelError(err?.message || t("booking.common.errorGeneric"));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleConfirmDelay = async () => {
    const orderId = getTicketOrderId(delayTarget);
    if (!orderId || delayLoading) return;
    setDelayLoading(true);
    setDelayError(null);
    setDelaySuccess(null);
    setDelayRecommendations([]);
    try {
      const payload = await createDelayTicket(lang, {
        orderId,
        minute: delayMinutes,
      });
      if (payload?.isSuccess) {
        await load();
        setDelayTarget(null);
        setDelayMinutes(DELAY_MINUTES[0]);
        setDelayError(null);
        setDelaySuccess(null);
        setDelayRecommendations([]);
        return;
      }
      const recommended = extractDelayRecommendations(payload);
      if (recommended.length) {
        setDelayRecommendations(recommended);
        setDelayError(
          payload?.message || t("booking.tickets.delayModal.recommended"),
        );
      } else {
        setDelayError(payload?.message || t("booking.common.errorGeneric"));
      }
    } catch (err) {
      console.error("[booking] delay failed", err);
      setDelayError(err?.message || t("booking.common.errorGeneric"));
    } finally {
      setDelayLoading(false);
    }
  };

  const bookingHref = lang === "en" ? "/en/online-booking" : "/online-booking";
  const canOpenRegister = !isAuthenticated && typeof setOpenRegisterModal === "function";

  const getTabCount = useCallback(
    (key) => {
      if (key === TAB_ACTIVE) return active.length;
      if (key === TAB_CANCELED) return canceled.length;
      return history.length;
    },
    [active.length, canceled.length, history.length],
  );

  const list =
    tab === TAB_ACTIVE ? active : tab === TAB_CANCELED ? canceled : history;
  const emptyKey =
    tab === TAB_ACTIVE ? "active" : tab === TAB_CANCELED ? "canceled" : "history";

  const cancelSuccessRepeatHref = lastCanceledTicket
    ? buildTicketBookingHref(lastCanceledTicket, {
        basePath: bookingHref,
        action: "repeat",
      })
    : bookingHref;

  return (
    <>
      <div
        className="booking-tickets-hero"
        style={{
          background: "var(--main-gradient)",
          padding: "clamp(40px, 7vw, 80px) 0 clamp(70px, 12vw, 120px) 0",
        }}
      >
        <div className="container container-two">
          <div className="booking-tickets-hero__row d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="booking-tickets-hero__copy" style={{ flex: 1, minWidth: 0 }}>
              <h1
                className="booking-tickets-hero__title fw-700 mb-2"
                style={{
                  color: "#fff",
                  fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
                  lineHeight: 1.15,
                }}
              >
                {t("booking.tickets.title")}
              </h1>
              <p
                className="mb-0"
                style={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontSize: "clamp(0.95rem, 1.5vw, 1.05rem)",
                }}
              >
                {t("booking.tickets.subtitle")}
              </p>
            </div>
            <Link
              to={bookingHref}
              className="booking-tickets-hero__action pill fw-600"
              style={{
                background: "#ffffff",
                border: "none",
                padding: "10px 22px",
                textDecoration: "none",
                boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
              }}
            >
              <span style={{ color: "#0f172a", fontWeight: 600 }}>
                <i
                  className="las la-plus me-2"
                  style={{ color: "hsl(166 100% 32%)" }}
                  aria-hidden="true"
                />
                {t("booking.page.newTicket")}
              </span>
            </Link>
          </div>
        </div>
      </div>

      <section
        className="booking-tickets-section"
        style={{ paddingBottom: "clamp(60px, 10vw, 140px)" }}
      >
        <div className="container container-two">
          <div
            style={{
              marginTop: -60,
              background: palette.surface,
              border: palette.border,
              borderRadius: 24,
              padding: "20px clamp(16px, 3vw, 28px)",
              boxShadow: palette.shadow,
            }}
          >
            <div className="d-flex gap-2 flex-wrap" role="tablist">
              {TICKET_TABS.map((key) => {
                const isActive = tab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className="fw-500"
                    style={{
                      background: isActive ? palette.accentSoft : "transparent",
                      border: isActive
                        ? `1px solid ${palette.accentBorder}`
                        : palette.border,
                      color: isActive ? palette.accent : palette.textMuted,
                      borderRadius: 999,
                      padding: "8px 18px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                    }}
                    role="tab"
                    aria-selected={isActive}
                  >
                    {t(`booking.tickets.tabs.${key}`)}
                    <span
                      className="ms-2"
                      style={{
                        background: isActive ? palette.accent : palette.surfaceAlt,
                        color: isActive ? "#fff" : palette.textMuted,
                        borderRadius: 999,
                        padding: "1px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}
                    >
                      {getTabCount(key)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            {loading ? (
              <div className="booking-tickets-grid">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      height: 140,
                      borderRadius: 16,
                      background: palette.surfaceAlt,
                      border: palette.border,
                    }}
                  />
                ))}
              </div>
            ) : error ? (
              <StepCard palette={palette}>
                <div style={{ padding: "clamp(14px, 4vw, 16px) clamp(16px, 4vw, 24px)" }}>
                  <EmptyState
                    palette={palette}
                    icon="las la-exclamation-circle"
                    title={t("booking.common.errorGeneric")}
                    action={<RetryButton palette={palette} onClick={load} />}
                  />
                </div>
              </StepCard>
            ) : list.length === 0 ? (
              <StepCard palette={palette}>
                <div style={{ padding: "clamp(24px, 5vw, 32px) clamp(16px, 4vw, 24px)" }}>
                  <div
                    className="booking-tickets-empty text-center mx-auto"
                    style={{
                      maxWidth: 560,
                      display: "grid",
                      justifyItems: "center",
                      gap: 14,
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        width: 86,
                        height: 86,
                        borderRadius: 28,
                        display: "grid",
                        placeItems: "center",
                        background: palette.accentSoft,
                        border: `1px solid ${palette.accentBorder}`,
                        boxShadow: palette.shadowSoft,
                      }}
                    >
                      <i
                        className={
                          tab === TAB_ACTIVE
                            ? "las la-calendar-check"
                            : tab === TAB_CANCELED
                              ? "las la-calendar-times"
                              : "las la-history"
                        }
                        style={{ color: palette.accent, fontSize: 42 }}
                      />
                    </div>
                    <div>
                      <h3
                        className="fw-800 mb-2"
                        style={{ color: palette.text, fontSize: "clamp(1.08rem, 3vw, 1.35rem)" }}
                      >
                        {isAuthenticated
                          ? t(`booking.tickets.empty.${emptyKey}`)
                          : t("booking.accountPrompt.title")}
                      </h3>
                      <p
                        className="mb-0"
                        style={{
                          color: palette.textMuted,
                          lineHeight: 1.55,
                          fontSize: "0.95rem",
                        }}
                      >
                        {isAuthenticated
                          ? t(`booking.tickets.emptySubtitle.${emptyKey}`)
                          : t("booking.accountPrompt.text")}
                      </p>
                    </div>
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      {canOpenRegister ? (
                        <button
                          type="button"
                          onClick={() => setOpenRegisterModal(true)}
                          className="pill fw-600"
                          style={{
                            background: "transparent",
                            color: palette.text,
                            border: palette.border,
                            padding: "10px 22px",
                          }}
                        >
                          <i className="las la-user-plus me-1" aria-hidden="true" />
                          {t("booking.accountPrompt.button")}
                        </button>
                      ) : null}
                      <Link
                        to={bookingHref}
                        className="pill fw-700"
                        style={{
                          background: palette.accent,
                          color: "#061415",
                          border: "none",
                          padding: "11px 26px",
                          textDecoration: "none",
                          boxShadow: palette.shadowSoft,
                        }}
                      >
                        <i className="las la-calendar-plus me-1" aria-hidden="true" />
                        {t("booking.tickets.bookAppointment")}
                      </Link>
                    </div>
                  </div>
                </div>
              </StepCard>
            ) : (
              <div className="booking-tickets-grid">
                {list.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    palette={palette}
                    ticket={ticket}
                    status={tab}
                    lang={lang}
                    onCancel={tab === TAB_ACTIVE ? handleCancel : undefined}
                    onDelay={tab === TAB_ACTIVE ? handleDelay : undefined}
                    repeatHref={
                      tab !== TAB_ACTIVE
                        ? buildTicketBookingHref(ticket, {
                            basePath: bookingHref,
                            action: "repeat",
                          })
                        : undefined
                    }
                    rescheduleHref={
                      tab === TAB_ACTIVE
                        ? buildTicketBookingHref(ticket, {
                            basePath: bookingHref,
                            action: "reschedule",
                          })
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {cancelTarget ? (
        <TicketModal
          palette={palette}
          icon="las la-calendar-times"
          title={t("booking.tickets.cancelModal.title")}
          message={t("booking.tickets.cancelModal.message")}
          onClose={() => {
            if (!cancelLoading) {
              setCancelTarget(null);
              setCancelError(null);
            }
          }}
        >
          {cancelError ? (
            <div
              className="mb-3"
              style={{
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.35)",
                color: "hsl(0 75% 60%)",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: "0.9rem",
              }}
            >
              {cancelError}
            </div>
          ) : null}
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <button
              type="button"
              onClick={() => {
                if (!cancelLoading) {
                  setCancelTarget(null);
                  setCancelError(null);
                }
              }}
              className="pill fw-500"
              disabled={cancelLoading}
              style={{
                background: "transparent",
                border: palette.border,
                color: palette.text,
                padding: "10px 20px",
              }}
            >
              {t("booking.common.back")}
            </button>
            <button
              type="button"
              onClick={handleConfirmCancel}
              className="pill fw-600"
              disabled={cancelLoading}
              style={{
                background: "hsl(0 75% 55%)",
                border: "none",
                color: "#fff",
                padding: "10px 22px",
                opacity: cancelLoading ? 0.72 : 1,
              }}
            >
              <i className="las la-times me-1" aria-hidden="true" />
              {cancelLoading
                ? t("booking.tickets.cancelModal.cancelling")
                : t("booking.tickets.cancelModal.confirm")}
            </button>
          </div>
        </TicketModal>
      ) : null}

      {delayTarget ? (
        <TicketModal
          palette={palette}
          icon="las la-clock"
          title={t("booking.tickets.delayModal.title", {
            time: formatTicketClock(delayTarget?.date) || "10։15",
          })}
          message={t("booking.tickets.delayModal.message")}
          onClose={() => {
            if (!delayLoading) {
              setDelayTarget(null);
              setDelayError(null);
              setDelaySuccess(null);
              setDelayRecommendations([]);
            }
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 8,
            }}
          >
            {DELAY_MINUTES.map((minute) => {
              const selected = delayMinutes === minute;
              return (
                <button
                  key={minute}
                  type="button"
                  onClick={() => setDelayMinutes(minute)}
                  disabled={delayLoading}
                  className="fw-700"
                  style={{
                    borderRadius: 14,
                    border: selected
                      ? `1px solid ${palette.accentBorder}`
                      : palette.border,
                    background: selected ? palette.accentSoft : palette.surfaceAlt,
                    color: selected ? palette.accent : palette.text,
                    padding: "12px 8px",
                    cursor: "pointer",
                  }}
                >
                  +{minute}
                  <span
                    className="d-block fw-500"
                    style={{ fontSize: "0.75rem", color: palette.textMuted }}
                  >
                    {t("booking.tickets.delayModal.minutes")}
                  </span>
                </button>
              );
            })}
          </div>
          {delayError ? (
            <div
              className="mt-3"
              style={{
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.35)",
                color: "hsl(0 75% 60%)",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: "0.9rem",
              }}
            >
              {delayError}
            </div>
          ) : null}
          {delayRecommendations.length ? (
            <div
              className="mt-3"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {delayRecommendations.map((item, index) => {
                const label = formatDelayRecommendation(item);
                return label ? (
                  <span
                    key={`${label}-${index}`}
                    className="fw-600"
                    style={{
                      background: palette.surfaceAlt,
                      border: palette.border,
                      borderRadius: 999,
                      color: palette.text,
                      padding: "8px 12px",
                      fontSize: "0.86rem",
                    }}
                  >
                    <i className="las la-clock me-1" aria-hidden="true" />
                    {label}
                  </span>
                ) : null;
              })}
            </div>
          ) : null}
          {delaySuccess ? (
            <div
              className="mt-3"
              style={{
                background: palette.accentSoft,
                border: `1px solid ${palette.accentBorder}`,
                color: palette.accent,
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: "0.9rem",
                fontWeight: 600,
              }}
            >
              <i className="las la-check me-1" aria-hidden="true" />
              {delaySuccess}
            </div>
          ) : null}
          <div className="d-flex flex-wrap gap-2 justify-content-center mt-4">
            <button
              type="button"
              onClick={() => {
                if (!delayLoading) {
                  setDelayTarget(null);
                  setDelayError(null);
                  setDelaySuccess(null);
                  setDelayRecommendations([]);
                }
              }}
              className="pill fw-500"
              disabled={delayLoading}
              style={{
                background: "transparent",
                border: palette.border,
                color: palette.text,
                padding: "10px 20px",
              }}
            >
              {t("booking.common.back")}
            </button>
            <button
              type="button"
              onClick={handleConfirmDelay}
              className="pill fw-600"
              disabled={delayLoading || Boolean(delaySuccess)}
              style={{
                background: palette.accent,
                border: "none",
                color: "#fff",
                padding: "10px 22px",
                opacity: delayLoading || delaySuccess ? 0.72 : 1,
              }}
            >
              <i className="las la-clock me-1" aria-hidden="true" />
              {delayLoading
                ? t("booking.tickets.delayModal.saving")
                : t("booking.tickets.delayModal.confirm")}
            </button>
          </div>
        </TicketModal>
      ) : null}

      {cancelSuccessOpen ? (
        <TicketModal
          palette={palette}
          icon="las la-check"
          title={t("booking.tickets.cancelSuccess.title")}
          message={t("booking.tickets.cancelSuccess.message")}
          onClose={() => setCancelSuccessOpen(false)}
        >
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <Link
              to={cancelSuccessRepeatHref}
              target="_blank"
              rel="noopener noreferrer"
              className="pill fw-600"
              style={{
                background: "transparent",
                border: palette.border,
                color: palette.text,
                padding: "10px 20px",
                textDecoration: "none",
              }}
            >
              <i className="las la-calendar-plus me-1" aria-hidden="true" />
              {t("booking.tickets.cancelSuccess.reschedule")}
            </Link>
            <button
              type="button"
              onClick={() => setCancelSuccessOpen(false)}
              className="pill fw-600"
              style={{
                background: palette.accent,
                border: "none",
                color: "#061415",
                padding: "10px 22px",
              }}
            >
              {t("booking.successModal.close")}
            </button>
          </div>
        </TicketModal>
      ) : null}

      <style>
        {`
          .booking-tickets-hero,
          .booking-tickets-hero *,
          .booking-tickets-section,
          .booking-tickets-section *,
          .booking-ticket-card,
          .booking-ticket-card *,
          .booking-tickets-empty,
          .booking-tickets-empty * {
            font-family: "Segoe UI", "Noto Sans Armenian", Arial, sans-serif !important;
          }

          .booking-tickets-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px;
            align-items: start;
          }

          .booking-ticket-card {
            min-width: 0;
          }

          .booking-ticket-card__hero {
            min-height: 124px !important;
          }

          @media (max-width: 991px) {
            .booking-tickets-grid {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 575px) {
            .booking-tickets-hero {
              padding: 34px 0 72px 0 !important;
            }

            .booking-tickets-hero .container {
              padding-left: 16px !important;
              padding-right: 16px !important;
            }

            .booking-tickets-hero__row {
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 14px !important;
            }

            .booking-tickets-hero__copy {
              width: 100% !important;
              min-width: 0 !important;
              flex: 0 0 auto !important;
            }

            .booking-tickets-hero__title {
              max-width: 100% !important;
              font-size: 1.65rem !important;
              line-height: 1.15 !important;
              word-break: normal !important;
              overflow-wrap: normal !important;
              hyphens: none !important;
            }

            .booking-tickets-hero__action {
              align-self: flex-start !important;
              min-height: 42px !important;
              padding: 9px 18px !important;
              white-space: nowrap !important;
            }

            .booking-tickets-grid {
              gap: 12px;
            }

            .booking-ticket-card {
              border-radius: 18px !important;
            }

            .booking-ticket-card__hero {
              min-height: 134px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default MyTicketsPage;
